export type OnlineStatusFilter = "any" | "online" | "offline";
export type VerificationFilter = "any" | "verified" | "unverified";
export type CreatorStatusFilter = "any" | "new" | "trending" | "top";

export interface AdvancedCreatorFilters {
  location: string;
  ethnicity: string;
  ageMin: string;
  ageMax: string;
  bodyType: string;
  orientation: string;
  onlineStatus: OnlineStatusFilter;
  verification: VerificationFilter;
  subscriptionPriceMin: string;
  subscriptionPriceMax: string;
  ppvPriceMin: string;
  ppvPriceMax: string;
  creatorStatus: CreatorStatusFilter;
  categories: string;
}

export const defaultAdvancedCreatorFilters: AdvancedCreatorFilters = {
  location: "",
  ethnicity: "",
  ageMin: "",
  ageMax: "",
  bodyType: "",
  orientation: "",
  onlineStatus: "any",
  verification: "any",
  subscriptionPriceMin: "",
  subscriptionPriceMax: "",
  ppvPriceMin: "",
  ppvPriceMax: "",
  creatorStatus: "any",
  categories: "",
};

export const ADVANCED_FILTER_KEYS: Array<keyof AdvancedCreatorFilters> = [
  "location",
  "ethnicity",
  "ageMin",
  "ageMax",
  "bodyType",
  "orientation",
  "onlineStatus",
  "verification",
  "subscriptionPriceMin",
  "subscriptionPriceMax",
  "ppvPriceMin",
  "ppvPriceMax",
  "creatorStatus",
  "categories",
];

const toNumberIfPossible = (value: string) => {
  if (!value || !value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const serializeAdvancedFilters = (filters: AdvancedCreatorFilters) => {
  const payload: Record<string, string | number> = {};
  if (filters.location.trim()) payload.location = filters.location.trim();
  if (filters.ethnicity.trim()) payload.ethnicity = filters.ethnicity.trim();
  if (filters.bodyType.trim()) payload.bodyType = filters.bodyType.trim();
  if (filters.orientation.trim()) payload.orientation = filters.orientation.trim();
  if (filters.categories.trim()) payload.categories = filters.categories.trim();
  if (filters.onlineStatus !== "any") payload.onlineStatus = filters.onlineStatus;
  if (filters.verification !== "any") payload.verification = filters.verification;
  if (filters.creatorStatus !== "any") payload.creatorStatus = filters.creatorStatus;

  const ageMin = toNumberIfPossible(filters.ageMin);
  const ageMax = toNumberIfPossible(filters.ageMax);
  const subscriptionPriceMin = toNumberIfPossible(filters.subscriptionPriceMin);
  const subscriptionPriceMax = toNumberIfPossible(filters.subscriptionPriceMax);
  const ppvPriceMin = toNumberIfPossible(filters.ppvPriceMin);
  const ppvPriceMax = toNumberIfPossible(filters.ppvPriceMax);

  if (ageMin !== undefined) payload.ageMin = ageMin;
  if (ageMax !== undefined) payload.ageMax = ageMax;
  if (subscriptionPriceMin !== undefined) payload.subscriptionPriceMin = subscriptionPriceMin;
  if (subscriptionPriceMax !== undefined) payload.subscriptionPriceMax = subscriptionPriceMax;
  if (ppvPriceMin !== undefined) payload.ppvPriceMin = ppvPriceMin;
  if (ppvPriceMax !== undefined) payload.ppvPriceMax = ppvPriceMax;

  return payload;
};

export const parseAdvancedFiltersFromSearch = (
  search: string
): AdvancedCreatorFilters => {
  const params = new URLSearchParams(search);
  const parsed: AdvancedCreatorFilters = { ...defaultAdvancedCreatorFilters };

  ADVANCED_FILTER_KEYS.forEach(key => {
    const value = params.get(key);
    if (value == null || value === "") return;
    (parsed as any)[key] = value;
  });

  if (!["any", "online", "offline"].includes(parsed.onlineStatus)) {
    parsed.onlineStatus = "any";
  }
  if (!["any", "verified", "unverified"].includes(parsed.verification)) {
    parsed.verification = "any";
  }
  if (!["any", "new", "trending", "top"].includes(parsed.creatorStatus)) {
    parsed.creatorStatus = "any";
  }

  return parsed;
};
